import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

const SIZE_PREFERENCE_STORAGE_KEY = 'horizon:selected-size';
const BUNDLE_SIZE_CONTEXT_STORAGE_KEY = 'horizon-bundle:size-context';
const SELECT_SIZE_LABEL = 'Select a size';

function normalizeSizeValue(value) {
  const text = String(value || '').trim();
  const match = text.match(/^(?:size\s*)?([0-7])(?:\b|\s|\()/i);

  return match ? match[1] : text.toLowerCase();
}

function getStoredSizePreference() {
  const storageCandidates = [
    { storage: window.localStorage, key: SIZE_PREFERENCE_STORAGE_KEY },
    { storage: window.sessionStorage, key: BUNDLE_SIZE_CONTEXT_STORAGE_KEY },
  ];

  for (const candidate of storageCandidates) {
    const normalizedValue = getStoredNormalizedSize(candidate.storage, candidate.key);
    if (normalizedValue) return normalizedValue;
  }

  return null;
}

function getStoredNormalizedSize(storage, key) {
  try {
    const rawValue = storage.getItem(key);
    if (!rawValue) return null;

    try {
      const parsed = JSON.parse(rawValue);
      const normalizedValue = normalizeSizeValue(parsed?.value || parsed?.size || '');

      return normalizedValue ? normalizedValue : null;
    } catch (_error) {
      const normalizedValue = normalizeSizeValue(rawValue);

      return normalizedValue ? normalizedValue : null;
    }
  } catch (_error) {
    return null;
  }
}

function getOptionValue(option) {
  if (option instanceof HTMLInputElement) {
    return option.value || option.getAttribute('aria-label') || '';
  }

  if (option instanceof HTMLOptionElement) {
    return option.value || option.textContent || '';
  }

  return '';
}

function getSizeControl(root) {
  if (!(root instanceof Element)) return null;

  const explicitFieldset = root.querySelector('variant-picker fieldset[data-option-is-size="true"]');
  if (explicitFieldset instanceof HTMLElement) return explicitFieldset;

  const explicitSelect = root.querySelector('variant-picker select[data-option-is-size="true"]');
  if (explicitSelect instanceof HTMLElement) return explicitSelect;

  const fallbackFieldsets = Array.from(root.querySelectorAll('variant-picker fieldset[data-option-name]'));
  for (const fieldset of fallbackFieldsets) {
    const optionName = fieldset.getAttribute('data-option-name')?.toLowerCase() || '';
    if (optionName.includes('size')) return fieldset;
  }

  const fallbackSelects = Array.from(root.querySelectorAll('variant-picker select[data-option-name]'));
  for (const select of fallbackSelects) {
    const optionName = select.getAttribute('data-option-name')?.toLowerCase() || '';
    if (optionName.includes('size')) return select;
  }

  return null;
}

function getSizeSelectionState(root) {
  const sizeControl = getSizeControl(root);
  if (!(sizeControl instanceof HTMLElement)) {
    return { required: false, selected: true };
  }

  if (sizeControl instanceof HTMLSelectElement) {
    return {
      required: true,
      selected: Boolean(sizeControl.value),
    };
  }

  return {
    required: true,
    selected: Boolean(sizeControl.querySelector('input:checked')),
  };
}

function getLabelNode(button) {
  const labelWrapper = button?.querySelector('.add-to-cart-text__content');
  if (!(labelWrapper instanceof HTMLElement)) return null;

  const labelNode = labelWrapper.querySelector(':scope > span:first-child');
  if (labelNode instanceof HTMLElement) return labelNode;

  const fallbackLabelNode = document.createElement('span');
  labelWrapper.prepend(fallbackLabelNode);

  return fallbackLabelNode;
}

function setButtonLabel(button, label) {
  const labelNode = getLabelNode(button);
  if (!(labelNode instanceof HTMLElement)) return;

  labelNode.textContent = label;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);
    return this.#withSelectedVariant(url);
  }

  get fallbackProductPageUrl() {
    if (!this.dataset.productUrl) return '';

    const url = new URL(this.dataset.productUrl, window.location.origin);
    return this.#withSelectedVariant(url);
  }

  /**
   * Adds the selected product card variant to a URL when available.
   * @param {URL} url
   * @returns {string}
   */
  #withSelectedVariant(url) {
    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl || this.fallbackProductPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (!productGrid) return;

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#applyStoredSizeSelection(modalContent);
    this.#syncSizeRequiredAddState(modalContent);
    this.#bindSizeRequiredState(modalContent);
  }

  #applyStoredSizeSelection(modalContent) {
    const preferredSize = getStoredSizePreference();
    if (!preferredSize) return;

    const sizeControl = getSizeControl(modalContent);
    if (!(sizeControl instanceof HTMLElement)) return;

    if (sizeControl instanceof HTMLSelectElement) {
      const preferredOption = Array.from(sizeControl.options).find(
        (option) => normalizeSizeValue(getOptionValue(option)) === preferredSize && !option.disabled
      );

      if (!preferredOption || sizeControl.value === preferredOption.value) return;

      sizeControl.value = preferredOption.value;
      sizeControl.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const preferredInput = Array.from(sizeControl.querySelectorAll('input')).find(
      (input) => input instanceof HTMLInputElement && !input.disabled && normalizeSizeValue(getOptionValue(input)) === preferredSize
    );

    if (!(preferredInput instanceof HTMLInputElement) || preferredInput.checked) return;

    preferredInput.click();
  }

  #syncSizeRequiredAddState(root) {
    const sizeState = getSizeSelectionState(root);
    const productForms = root.querySelectorAll('product-form-component');

    for (const productForm of productForms) {
      const button = productForm.querySelector('[ref="addToCartButton"]');
      if (!(button instanceof HTMLButtonElement)) continue;

      if (button.dataset.quickAddNativeLabel == null) {
        const labelNode = getLabelNode(button);
        const nativeLabel = labelNode?.textContent?.trim() || '';
        if (nativeLabel && nativeLabel !== SELECT_SIZE_LABEL) {
          button.dataset.quickAddNativeLabel = nativeLabel;
        }
      }

      if (sizeState.required && !sizeState.selected) {
        setButtonLabel(button, SELECT_SIZE_LABEL);
        button.disabled = true;
        button.dataset.quickAddSizeRequired = 'true';
        continue;
      }

      if (button.dataset.quickAddSizeRequired === 'true') {
        const nativeLabel = button.dataset.quickAddNativeLabel;
        if (nativeLabel) {
          setButtonLabel(button, nativeLabel);
        }
      }

      delete button.dataset.quickAddSizeRequired;
    }
  }

  #bindSizeRequiredState(modalContent) {
    if (modalContent.dataset.quickAddSizeGuardBound === 'true') return;

    modalContent.dataset.quickAddSizeGuardBound = 'true';
    modalContent.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.closest('variant-picker')) return;

      this.#syncSizeRequiredAddState(modalContent);
    });

    modalContent.addEventListener(ThemeEvents.variantUpdate, () => {
      requestAnimationFrame(() => this.#syncSizeRequiredAddState(modalContent));
    });
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
